// Serviço para gerenciar fornecedores no Supabase
import { supabase } from '../lib/supabase';
import type { Fornecedor, Endereco } from '../types';

// Interface para fornecedor no banco de dados
interface FornecedorDB {
  id: string;
  nome: string;
  cnpj: string;
  email: string;
  telefone: string;
  endereco_id: string;
  conta_id: string;
  empresa_id: string;
  created_at: string;
  updated_at: string;
}

// Converter fornecedor do banco para o formato da aplicação
function fornecedorDBToFornecedor(fornecedorDB: FornecedorDB, endereco: Endereco): Fornecedor {
  return {
    id: fornecedorDB.id,
    nome: fornecedorDB.nome,
    cnpj: fornecedorDB.cnpj,
    email: fornecedorDB.email,
    telefone: fornecedorDB.telefone,
    endereco,
    createdAt: fornecedorDB.created_at,
    updatedAt: fornecedorDB.updated_at,
  };
}

// Buscar todos os fornecedores da empresa atual
export async function getFornecedores(empresaId: string): Promise<Fornecedor[]> {
  try {
    // Buscar fornecedores sem join para evitar recursão RLS
    const { data: fornecedores, error: fornecedoresError } = await supabase
      .from('fornecedores')
      .select('id, nome, cnpj, email, telefone, endereco_id, created_at, updated_at')
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false });

    if (fornecedoresError) {
      console.error('[fornecedoresService] Erro ao buscar fornecedores:', fornecedoresError);
      return [];
    }

    if (!fornecedores || fornecedores.length === 0) {
      return [];
    }

    // Buscar endereços separadamente para evitar recursão
    const enderecoIds = fornecedores.map(f => f.endereco_id).filter(Boolean);
    const enderecosMap = new Map<string, Endereco>();

    if (enderecoIds.length > 0) {
      const { data: enderecos, error: enderecosError } = await supabase
        .from('enderecos')
        .select('*')
        .in('id', enderecoIds);

      if (!enderecosError && enderecos) {
        enderecos.forEach((e: any) => {
          enderecosMap.set(e.id, {
            cep: e.cep || '',
            logradouro: e.logradouro || '',
            numero: e.numero || '',
            complemento: e.complemento || undefined,
            bairro: e.bairro || '',
            cidade: e.cidade || '',
            estado: e.estado || '',
          });
        });
      }
    }

    // Montar fornecedores com endereços
    return fornecedores.map((f: any) => {
      const endereco = enderecosMap.get(f.endereco_id) || {
        cep: '',
        logradouro: '',
        numero: '',
        bairro: '',
        cidade: '',
        estado: '',
      };

      return fornecedorDBToFornecedor({ ...f, endereco_id: f.endereco_id }, endereco);
    });
  } catch (error) {
    console.error('[fornecedoresService] Erro geral ao buscar fornecedores:', error);
    return [];
  }
}

// Buscar fornecedor por ID
export async function getFornecedorById(id: string): Promise<Fornecedor | null> {
  try {
    const { data: fornecedor, error } = await supabase
      .from('fornecedores')
      .select('*, enderecos(*)')
      .eq('id', id)
      .single();

    if (error) throw error;
    if (!fornecedor) return null;

    const endereco: Endereco = {
      cep: fornecedor.enderecos?.cep || '',
      logradouro: fornecedor.enderecos?.logradouro || '',
      numero: fornecedor.enderecos?.numero || '',
      complemento: fornecedor.enderecos?.complemento || undefined,
      bairro: fornecedor.enderecos?.bairro || '',
      cidade: fornecedor.enderecos?.cidade || '',
      estado: fornecedor.enderecos?.estado || '',
    };

    return fornecedorDBToFornecedor(fornecedor, endereco);
  } catch (error) {
    console.error('Erro ao buscar fornecedor:', error);
    throw error;
  }
}

// Criar novo fornecedor
export async function createFornecedor(
  fornecedor: Omit<Fornecedor, 'id' | 'createdAt' | 'updatedAt'>,
  contaId: string,
  empresaId: string
): Promise<Fornecedor> {
  try {
    // Primeiro, criar o endereço
    const { data: endereco, error: enderecoError } = await supabase
      .from('enderecos')
      .insert({
        cep: fornecedor.endereco.cep,
        logradouro: fornecedor.endereco.logradouro,
        numero: fornecedor.endereco.numero,
        complemento: fornecedor.endereco.complemento || null,
        bairro: fornecedor.endereco.bairro,
        cidade: fornecedor.endereco.cidade,
        estado: fornecedor.endereco.estado,
      })
      .select()
      .single();

    if (enderecoError) throw enderecoError;

    // Depois, criar o fornecedor
    const { data: novoFornecedor, error: fornecedorError } = await supabase
      .from('fornecedores')
      .insert({
        nome: fornecedor.nome,
        cnpj: fornecedor.cnpj,
        email: fornecedor.email,
        telefone: fornecedor.telefone,
        endereco_id: endereco.id,
        conta_id: contaId,
        empresa_id: empresaId,
      })
      .select()
      .single();

    if (fornecedorError) throw fornecedorError;

    return fornecedorDBToFornecedor(novoFornecedor, fornecedor.endereco);
  } catch (error) {
    console.error('Erro ao criar fornecedor:', error);
    throw error;
  }
}

// Atualizar fornecedor
export async function updateFornecedor(id: string, fornecedor: Partial<Fornecedor>): Promise<Fornecedor> {
  try {
    // Buscar fornecedor atual para obter endereco_id
    const { data: fornecedorAtual, error: buscaError } = await supabase
      .from('fornecedores')
      .select('endereco_id, enderecos(*)')
      .eq('id', id)
      .single();

    if (buscaError) throw buscaError;

    // Atualizar endereço se fornecido
    if (fornecedor.endereco && fornecedorAtual.endereco_id) {
      const { error: enderecoError } = await supabase
        .from('enderecos')
        .update({
          cep: fornecedor.endereco.cep,
          logradouro: fornecedor.endereco.logradouro,
          numero: fornecedor.endereco.numero,
          complemento: fornecedor.endereco.complemento || null,
          bairro: fornecedor.endereco.bairro,
          cidade: fornecedor.endereco.cidade,
          estado: fornecedor.endereco.estado,
        })
        .eq('id', fornecedorAtual.endereco_id);

      if (enderecoError) throw enderecoError;
    }

    // Atualizar fornecedor
    const updateData: any = {};
    if (fornecedor.nome !== undefined) updateData.nome = fornecedor.nome;
    if (fornecedor.cnpj !== undefined) updateData.cnpj = fornecedor.cnpj;
    if (fornecedor.email !== undefined) updateData.email = fornecedor.email;
    if (fornecedor.telefone !== undefined) updateData.telefone = fornecedor.telefone;

    const { data: fornecedorAtualizado, error: fornecedorError } = await supabase
      .from('fornecedores')
      .update(updateData)
      .eq('id', id)
      .select('*, enderecos(*)')
      .single();

    if (fornecedorError) throw fornecedorError;

    const endereco: Endereco = {
      cep: fornecedorAtualizado.enderecos?.cep || '',
      logradouro: fornecedorAtualizado.enderecos?.logradouro || '',
      numero: fornecedorAtualizado.enderecos?.numero || '',
      complemento: fornecedorAtualizado.enderecos?.complemento || undefined,
      bairro: fornecedorAtualizado.enderecos?.bairro || '',
      cidade: fornecedorAtualizado.enderecos?.cidade || '',
      estado: fornecedorAtualizado.enderecos?.estado || '',
    };

    return fornecedorDBToFornecedor(fornecedorAtualizado, endereco);
  } catch (error) {
    console.error('Erro ao atualizar fornecedor:', error);
    throw error;
  }
}

// Deletar fornecedor
export async function deleteFornecedor(id: string): Promise<void> {
  try {
    // Buscar fornecedor para obter endereco_id
    const { data: fornecedor, error: buscaError } = await supabase
      .from('fornecedores')
      .select('endereco_id')
      .eq('id', id)
      .single();

    if (buscaError) throw buscaError;

    // Deletar fornecedor
    const { error: fornecedorError } = await supabase
      .from('fornecedores')
      .delete()
      .eq('id', id);

    if (fornecedorError) throw fornecedorError;

    // Deletar endereço se não estiver sendo usado
    if (fornecedor.endereco_id) {
      const { error: enderecoError } = await supabase
        .from('enderecos')
        .delete()
        .eq('id', fornecedor.endereco_id);

      // Não lançar erro se o endereço estiver sendo usado por outros registros
      if (enderecoError && !enderecoError.message.includes('violates foreign key')) {
        console.warn('Erro ao deletar endereço:', enderecoError);
      }
    }
  } catch (error) {
    console.error('Erro ao deletar fornecedor:', error);
    throw error;
  }
}

// Buscar fornecedores por termo (para busca global)
export async function searchFornecedores(term: string, empresaId: string): Promise<Fornecedor[]> {
  try {
    const termLower = term.toLowerCase();
    
    const { data: fornecedores, error } = await supabase
      .from('fornecedores')
      .select('*, enderecos(*)')
      .eq('empresa_id', empresaId)
      .or(`nome.ilike.%${termLower}%,cnpj.ilike.%${term}%,email.ilike.%${termLower}%`)
      .limit(20);

    if (error) throw error;

    return (fornecedores || []).map((f: any) => {
      const endereco: Endereco = {
        cep: f.enderecos?.cep || '',
        logradouro: f.enderecos?.logradouro || '',
        numero: f.enderecos?.numero || '',
        complemento: f.enderecos?.complemento || undefined,
        bairro: f.enderecos?.bairro || '',
        cidade: f.enderecos?.cidade || '',
        estado: f.enderecos?.estado || '',
      };

      return fornecedorDBToFornecedor(f, endereco);
    });
  } catch (error) {
    console.error('Erro ao buscar fornecedores:', error);
    throw error;
  }
}

